<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import {
  installState,
  promptInstall,
  dismissBanner,
  bannerDismissed,
  INSTALLABLE_EVENT,
  INSTALLED_EVENT,
  type InstallState,
} from '../lib/install'
import { reachGoal } from '../lib/metrika'
import {
  useBannerSpace,
  usePagePromptPresence,
  pagePromptShown,
  BANNER_SLOT_CLASS,
  BANNER_CARD_CLASS,
} from '../composables/useBannerSpace'
import UiButton from './ui/UiButton.vue'

// Одно предложение установки в двух местах:
// banner — полоса внизу любой страницы личного кабинета, её можно закрыть. Показывает
// только то, на что можно отреагировать: кнопку установки (available) и совет про
// «Поделиться» на iOS (его крестик запоминается навсегда). Советы про меню браузера и
// про Chrome вместо «Яндекса» полоса сама не поднимает: человек ничего не может нажать,
// а возвращалась бы она каждый сеанс;
// panel — постоянный блок в профиле рядом с тумблером уведомлений, чтобы закрывший
// полосу человек всё равно мог установить приложение позже. Там все советы на месте.
//
// suppressed — нижнюю полосу забрало предложение включить уведомления (арбитраж в
// AppLayout.vue). Две полосы внизу одновременно — каша, а на Android и десктопе
// уведомления важнее: они дают функцию, установка — удобство. На iOS до этого не
// доходит, там предложение пуша само молчит без установки.
// На variant="panel" проп не действует: блок в профиле не полоса и ни с кем не спорит.
const { variant, suppressed = false } = defineProps<{
  variant: 'banner' | 'panel'
  suppressed?: boolean
}>()

const state = ref<InstallState>('unavailable')
const dismissed = ref(false)
const busy = ref(false)
// Человек нажал «Установить» в полосе, а окна не было (no_dialog): совет про меню
// браузера должен остаться на месте нажатой кнопки, иначе полоса молча исчезнет.
// Только в этом инстансе: в хранилище не пишем, при следующей загрузке полосы не будет.
const adviceAfterClick = ref(false)

const WITH_TEXT: InstallState[] = [
  'available',
  'ios_instructions',
  'manual_instructions',
  'external_browser_instructions',
]

const ADVICE_ONLY: InstallState[] = ['manual_instructions', 'external_browser_instructions']

const visible = computed(() => {
  if (!WITH_TEXT.includes(state.value)) return false
  if (variant !== 'banner') return true
  if (suppressed) return false
  // Советы без кнопки полоса показывает только в ответ на нажатие «Установить».
  if (ADVICE_ONLY.includes(state.value) && !adviceAfterClick.value) return false
  // Блок в профиле предлагает установку теми же словами и стоит в потоке страницы —
  // полоса поверх него была бы вторым таким же предложением на одном экране.
  // Просьба про уведомления полосу установки при этом не трогает: разные действия
  // (учёт по видам — см. usePagePromptPresence в useBannerSpace.ts).
  if (pagePromptShown('install')) return false

  return !dismissed.value
})

function sync(): void {
  state.value = installState()
  // Исключение «после нажатия» живёт, пока состояние остаётся советом: ушло в
  // available/installed — флаг не должен пережить это и вернуться позже.
  if (!ADVICE_ONLY.includes(state.value)) adviceAfterClick.value = false
  dismissed.value = bannerDismissed()
}

onMounted(() => {
  sync()
  // beforeinstallprompt браузер присылает, как правило, ПОСЛЕ монтирования Vue,
  // а установка приложения может случиться и из другой вкладки — поэтому состояние
  // пересчитываем по событиям окна, а не только на onMounted.
  window.addEventListener(INSTALLABLE_EVENT, sync)
  window.addEventListener(INSTALLED_EVENT, sync)
})

onUnmounted(() => {
  window.removeEventListener(INSTALLABLE_EVENT, sync)
  window.removeEventListener(INSTALLED_EVENT, sync)
})

async function install(): Promise<void> {
  busy.value = true
  try {
    const outcome = await promptInstall()
    if (outcome === 'installed') {
      // Цель отправляем на факт установки (appinstalled), а не на слово браузера:
      // форки Chromium отвечают «accepted», ничего не установив, и цель считала бы
      // установки, которых не было.
      reachGoal('pwa_install_accepted')
      // Предлагать установку тому, кто уже установил, не нужно ни в полосе, ни в профиле.
      state.value = 'installed'
    }
    if (outcome === 'no_dialog') {
      // Окна установки человек не увидел, и мы не знаем почему. Прятать предложение
      // нельзя — оно единственная зацепка; вместо кнопки объясняем путь через меню.
      state.value = 'manual_instructions'
      adviceAfterClick.value = true
    }
    if (outcome === 'dismissed') {
      reachGoal('pwa_install_dismissed')
      // Отказ в окне браузера — тот же сигнал, что и крестик: полосу до конца сеанса
      // убираем. Блок в профиле остаётся, человек сам решает, когда вернуться.
      dismissBanner()
      dismissed.value = true
    }
  } finally {
    busy.value = false
  }
}

function close(): void {
  // На iOS крестик навсегда (см. IOS_DISMISSED_KEY в install.ts), в остальных — на сеанс.
  dismissBanner(state.value === 'ios_instructions')
  dismissed.value = true
}

// Уйти из встроенного браузера приложения «Яндекс» нельзя ничем: ни пункт меню
// «Открыть во внешнем браузере», ни ссылка intent:// с package=com.android.chrome не
// передают навигацию наружу — приложение открывает всё новой вкладкой внутри себя
// (проверено на живом телефоне 2026-08-29). Поэтому единственный оставшийся путь —
// ручной: кладём адрес в буфер, дальше человек открывает Chrome и вставляет.
const copied = ref(false)
const copyFailed = ref(false)

// Кнопка есть не у всех состояний: у советов про меню браузера и про «Поделиться»
// нажимать нечего, там только текст.
const hasButton = computed(
  () => state.value === 'available'
    || (state.value === 'external_browser_instructions' && !copied.value),
)
const host = window.location.host

async function copyLink(): Promise<void> {
  try {
    await navigator.clipboard.writeText(window.location.href)
    copied.value = true
  } catch {
    // Буфер может быть недоступен: старый движок, отказ в разрешении, http вместо https.
    // Тогда показываем адрес текстом — его хотя бы можно набрать руками.
    copyFailed.value = true
  }
}

const root = ref<HTMLElement | null>(null)
// Переменную отступа занимает только полоса: блок в профиле стоит в потоке страницы
// и места поверх неё не отнимает. Развилка — экономия, а не защита от гонки: вклад в
// общую переменную считается по реестру всех инстансов сразу (см. useBannerSpace.ts),
// и лишний нулевой вклад ничего бы не сломал.
if (variant === 'banner') useBannerSpace(root, () => visible.value)
// Блок в профиле забирает право спрашивать про установку себе — по видимости, а не
// по монтированию: при state 'installed' он ничего не рисует, и уступать ему нечему.
if (variant === 'panel') usePagePromptPresence('install', () => visible.value)

const wrapperClass = computed(() => (variant === 'banner' ? BANNER_SLOT_CLASS : ''))
</script>

<template>
  <div v-if="visible" ref="root" data-testid="install-prompt" :class="wrapperClass">
    <!-- Карточка полосы — BANNER_CARD_CLASS из useBannerSpace.ts (см. комментарий
         там). Блок в профиле подложки не берёт — он и так внутри карточки. -->
    <div
      class="mx-auto flex w-full max-w-6xl flex-col gap-3 sm:flex-row sm:items-center sm:gap-4"
      :class="variant === 'banner' ? BANNER_CARD_CLASS : ''"
    >
      <!-- На узком экране текст и кнопка стоят друг под другом: в одну строку кнопка
           отжимает объяснение в колонку шириной в пару слов (проверено на телефоне
           2026-08-29). С sm: возвращается строка — там места хватает обоим. -->
      <div class="min-w-0 flex-1">
        <h2 v-if="variant === 'panel'" class="mb-3 text-lg font-semibold">Приложение на телефоне</h2>

        <!-- Полоса и блок объясняют одно и то же, но разной длины: полоса висит поверх
             страницы, и шесть строк текста на мобильном экране превращают её из полосы в
             ещё одну карточку, накрывающую содержимое. Подробности остаются блоку в
             профиле, куда человек пришёл сам. -->
        <p v-if="state === 'ios_instructions'" class="text-sm" :class="variant === 'banner' ? 'text-text' : 'text-muted'">
          <template v-if="variant === 'banner'">
            Добавьте сайт на домашний экран: «Поделиться» → «На экран „Домой“».
          </template>
          <template v-else>
            Чтобы открывать сайт как приложение, добавьте его на домашний экран: кнопка
            «Поделиться», затем «На экран „Домой“».
          </template>
        </p>
        <p v-else-if="state === 'external_browser_instructions'" class="text-sm" :class="variant === 'banner' ? 'text-text' : 'text-muted'">
          <template v-if="copied">
            Ссылка скопирована. Откройте Chrome и вставьте её в адресную строку: установка
            появится там.
          </template>
          <template v-else-if="copyFailed">
            Скопировать не вышло. Откройте в Chrome адрес {{ host }} и установите приложение
            оттуда.
          </template>
          <template v-else>
            Внутри приложения «Яндекс» установить нельзя — это умеет только Chrome.
            Скопируйте ссылку, откройте Chrome и вставьте её в адресную строку.
          </template>
        </p>
        <p v-else-if="state === 'manual_instructions'" class="text-sm" :class="variant === 'banner' ? 'text-text' : 'text-muted'">
          Этот браузер не показывает окно установки. Откройте меню браузера и выберите
          «Добавить на главный экран» или «Установить приложение».
        </p>
        <p v-else class="text-sm" :class="variant === 'banner' ? 'text-text' : 'text-muted'">
          <template v-if="variant === 'banner'">
            Установите приложение на телефон.
          </template>
          <template v-else>
            Сайт можно установить как приложение: открывается быстрее и работает без адресной
            строки браузера.
          </template>
        </p>
      </div>

      <!-- Кнопка и крестик — одна строка действий: на узком экране крестик стоит рядом
           с кнопкой, а не висит над её правым краем сам по себе. -->
      <div v-if="hasButton || variant === 'banner'" class="flex items-center justify-end gap-2 sm:flex-none">
        <UiButton
          v-if="state === 'external_browser_instructions' && !copied"
          data-testid="install-copy"
          class="flex-1 whitespace-nowrap sm:flex-none"
          @click="copyLink"
        >
          Скопировать ссылку
        </UiButton>

        <UiButton
          v-if="state === 'available'"
          data-testid="install-button"
          :loading="busy"
          class="flex-1 whitespace-nowrap sm:flex-none"
          @click="install"
        >
          Установить
        </UiButton>

        <button
          v-if="variant === 'banner'"
          type="button"
          data-testid="install-close"
          aria-label="Закрыть"
          class="flex h-9 w-9 flex-none items-center justify-center rounded-md text-muted hover:bg-accent/10"
          @click="close"
        >
          ✕
        </button>
      </div>
    </div>
  </div>
</template>
